import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Download, Share, X } from 'lucide-react';
import posthog from 'posthog-js';
import {
    useInstallPrompt,
    hasPractised,
    isPromptDismissed,
    dismissPrompt,
} from '../lib/installPrompt';

// Nudge to add Mimique to the home screen. One component, two routes: Chrome
// and Edge get a button that opens the real install dialog, iOS Safari gets the
// Share-sheet instructions, because Apple provides no install API.
//
// Held back until they've finished a practice session — before that they have
// no reason to want an icon, and a prompt they say no to is a prompt they can't
// easily be asked again.
const InstallPrompt = () => {
    const { canInstall, needsIosInstructions, installed, promptInstall } = useInstallPrompt();
    const [dismissed, setDismissed] = useState(true);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        setDismissed(isPromptDismissed());
    }, []);

    // Never on /install itself: that page already shows the steps, and the
    // pop-up sat on top of them.
    const show = pathname !== '/install' && !installed && !dismissed && hasPractised() && (canInstall || needsIosInstructions);

    useEffect(() => {
        if (show) posthog.capture('install_prompt_shown', { platform: canInstall ? 'android' : 'ios' });
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!show) return null;

    const close = () => {
        dismissPrompt();
        setDismissed(true);
        posthog.capture('install_prompt_dismissed');
    };

    const install = async () => {
        const accepted = await promptInstall();
        posthog.capture('install_prompt_answered', { accepted });
        if (accepted) close();
    };

    // iPhones get no install dialog, so the pop-up had nothing to tap and
    // people tapped it anyway (Nov 2026). It now opens the step-by-step page.
    const showSteps = () => {
        posthog.capture('install_prompt_steps_opened');
        navigate('/install');
    };

    return (
        <div
            role="note"
            // Clears the mobile bottom nav rather than sitting over it.
            className="fixed bottom-[68px] md:bottom-4 inset-x-3 md:inset-x-auto md:right-4 md:max-w-sm z-50 rounded-xl bg-white shadow-lg ring-1 ring-stone-200 p-3 flex items-start gap-3 animate-fade-in"
        >
            <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                {canInstall ? <Download className="h-4 w-4" /> : <Share className="h-4 w-4" />}
            </span>

            <div className="flex-1 min-w-0">
                <p className="text-xs leading-relaxed text-stone-700">
                    {/* Same wording as the Settings card, so the two read as
                        one thing said twice, not two different offers. */}
                    <strong className="block text-stone-900">Add Mimique to home screen</strong>
                    {canInstall
                        ? 'The full app on your home screen, straight to your practice.'
                        : 'A few taps in Safari and Mimique sits with your other apps.'}
                </p>
                <button
                    onClick={canInstall ? install : showSteps}
                    className="mt-2 inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
                >
                    {canInstall ? <Download className="h-3.5 w-3.5" /> : <Share className="h-3.5 w-3.5" />}
                    {canInstall ? 'Add to home screen' : 'Show me how'}
                </button>
            </div>

            <button
                onClick={close}
                className="text-stone-400 hover:text-stone-600 flex-shrink-0"
                aria-label="Dismiss"
            >
                <X className="h-4 w-4" />
            </button>
        </div>
    );
};

export default InstallPrompt;
